import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import {
  selectClientsData,
  selectClientsLoading,
  selectClientsError,
} from "../../Redux/Slices/clientsSlice";
import { selectProjectsData } from "../../Redux/Slices/projectsManagementSlice";
import {
  getClientsListFromApiData,
  getClientsMetaFromApiData,
  buildClientsListFetchParams,
  CLIENTS_LIST_PAGE_SIZE_STORAGE_KEY,
} from "./clientsDataUtils";
import { useListPageSize } from "../../hooks/useListPageSize";
import ClientDashboard from "./ClientDashboard";
import ClientList from "./ClientList";
import AddClient from "./AddClient";
import EditClient from "./EditClient";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import BackArrow from "../../Utility/BackArrow/BackArrow";
import AddButton from "../../Utility/AddButton/AddButton";
import { RiRefreshLine, RiUpload2Line } from "react-icons/ri";
import { downloadClientsExport } from "../../utils/downloadClientsExport";
import { showError as showExportError } from "../../utils/toast";
import { useAsyncResource } from "../../hooks/useAsyncResource";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import { getPageDeniedContent } from "../../permissions/permissionMessages";
import RequirePermission from "../../Utility/RequirePermission/RequirePermission";
import PermissionDenied from "../../Utility/PermissionDenied/PermissionDenied";
import { ROUTES } from "../../constants";

const Clients = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const me = useSelector((s) => s.auth?.me);
  const user = useSelector((s) => s.auth?.user);
  const profileResolved = useSelector((s) => s.auth?.profileResolved);
  const fetchMeLoading = useSelector((s) => s.auth?.loading?.fetchMe);
  const clientsData = useSelector(selectClientsData);
  const loading = useSelector(selectClientsLoading);
  const error = useSelector(selectClientsError);
  const { showError, errorMessage } = useAsyncResource(loading, error, clientsData);
  const projectsData = useSelector(selectProjectsData);

  const { pageSize: limit, setPageSize } = useListPageSize(CLIENTS_LIST_PAGE_SIZE_STORAGE_KEY);
  const activeView = useMemo(() => {
    const view = new URLSearchParams(location.search).get("view");
    return view === "dashboard" ? "dashboard" : "list";
  }, [location.search]);
  const [searchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [listingFilter, setListingFilter] = useState({
    clientName: "",
    status: "",
    contactPerson: "",
  });
  const [dashboardRefreshToken, setDashboardRefreshToken] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);

  const canViewClientDashboard = can(PERMISSION_KEYS.CLIENTS_DASHBOARD_VIEW);
  const canViewClientsList = can(PERMISSION_KEYS.CLIENT_VIEW);
  /** API `CLIENT_VIEW` (scoped list); not granted by `CLIENT_VIEW_ALL` alone — avoids list GET + toast 403. */
  const canUseClientsList = can(PERMISSION_KEYS.CLIENTS_SIDEBAR);
  const canCreateClient = can(PERMISSION_KEYS.CLIENTS_CREATE);
  const waitingForProfile = user != null && me == null && profileResolved === false;

  const fetchClientsWithFilters = (page = 1, filterOverrides = null) => {
    if (!canViewClientsList || !canUseClientsList) return;
    const f = filterOverrides ?? listingFilter;
    dispatch(fetchClientsData(buildClientsListFetchParams({ page, limit, listingFilter: f })));
  };

  useEffect(() => {
    if (waitingForProfile || (fetchMeLoading && !me)) return;
    if (!canViewClientsList || !canUseClientsList) return;
    dispatch(fetchClientsData(buildClientsListFetchParams({ page: 1, limit, listingFilter: {} })));
  }, [
    dispatch,
    limit,
    canViewClientsList,
    canUseClientsList,
    waitingForProfile,
    fetchMeLoading,
    me,
  ]);

  useEffect(() => {
    const isAddOrEdit =
      location.pathname === ROUTES.CLIENTS_ADD ||
      location.pathname.startsWith(`${ROUTES.CLIENTS_EDIT}/`);
    if (!canUseClientsList && !isAddOrEdit) return;
    dispatch(fetchProjectsData({ getAll: true }));
  }, [dispatch, canUseClientsList, location.pathname]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setListingFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterReset = () => {
    setListingFilter({
      clientName: "",
      status: "",
      contactPerson: "",
    });
    setCurrentPage(1);
    if (!canViewClientsList || !canUseClientsList) return;
    dispatch(
      fetchClientsData(
        buildClientsListFetchParams({
          page: 1,
          limit,
          listingFilter: { clientName: "", status: "", contactPerson: "" },
        })
      )
    );
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchClientsWithFilters(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchClientsWithFilters(page);
  };

  const handlePageSizeChange = (newLimit) => {
    setPageSize(newLimit);
    setCurrentPage(1);
    if (!canViewClientsList || !canUseClientsList) return;
    dispatch(
      fetchClientsData(buildClientsListFetchParams({ page: 1, limit: newLimit, listingFilter }))
    );
  };

  const handleExportClients = async () => {
    setExportLoading(true);
    try {
      await downloadClientsExport("csv");
    } catch (err) {
      showExportError(err?.message || "Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const list = useMemo(() => getClientsListFromApiData(clientsData), [clientsData]);
  const meta = getClientsMetaFromApiData(clientsData);

  if (showError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Typography variant="text1" className="text-red-600 text-lg">
            Error: {errorMessage}
          </Typography>
        </div>
      </div>
    );
  }

  // Get selected client (from query param or fallback)
  const searchParams = new URLSearchParams(location.search);
  const selectedClientId = searchParams.get("clientId");
  const selectedClient =
    list.find((client) => String(client.id) === String(selectedClientId)) || null;

  return (
    <div>
      {/* Hide Clients heading on Add/Edit Client form */}
      {location.pathname !== ROUTES.CLIENTS_ADD &&
        !location.pathname.startsWith(`${ROUTES.CLIENTS_EDIT}/`) &&
        !(location.pathname === ROUTES.CLIENTS && activeView === "list" && !canUseClientsList) && (
          <div className="w-full mb-4 flex flex-col sm:flex-row items-center justify-between gap-2 py-3">
            {/* Left side: Back arrow (only on dashboard) + title */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {activeView === "dashboard" && canViewClientDashboard && (
                  <BackArrow
                    to="/clients"
                    className="mt-[-5px]" // slight vertical align tweak
                  />
                )}
                <Typography variant="text1" className="text-xl font-semibold">
                  {activeView === "dashboard" &&
                  canViewClientDashboard &&
                  selectedClient?.clientName
                    ? selectedClient.clientName
                    : "Clients"}
                </Typography>
              </div>
              {activeView === "dashboard" &&
                canViewClientDashboard &&
                selectedClient?.clientName && (
                  <Typography variant="caption" className="text-xs text-gray-500 mt-0.5 block ml-1">
                    Client Dashboard
                  </Typography>
                )}
            </div>

            <div className="flex items-center gap-2">
              {activeView === "dashboard" && canViewClientDashboard && selectedClient?.id && (
                <Button
                  variant="neutral"
                  size="vsm"
                  title="Sync"
                  className="btn-reset square-btn"
                  onClick={() => setDashboardRefreshToken((prev) => prev + 1)}
                >
                  <RiRefreshLine className="text-lg text-text-secondary" />
                </Button>
              )}

              {/* Export all clients — list page, same visibility as the list itself */}
              {location.pathname === ROUTES.CLIENTS &&
                activeView === "list" &&
                canViewClientsList &&
                canUseClientsList && (
                  <Button
                    variant="neutral"
                    size="vsm"
                    title="Export"
                    data-tooltipp="Export"
                    className="btn-reset square-btn"
                    disabled={exportLoading}
                    onClick={handleExportClients}
                  >
                    <RiUpload2Line className="text-lg text-text-secondary" />
                  </Button>
                )}

              {/* Add Client button on list page - always visible (same as Users/Teams) */}
              {location.pathname === ROUTES.CLIENTS &&
                activeView === "list" &&
                canViewClientsList &&
                canUseClientsList &&
                canCreateClient && (
                  <AddButton
                    onClick={() =>
                      navigate("/clients/add", {
                        state: {
                          previousPath: `${location.pathname}${location.search || ""}`,
                          clientsFetchParams: buildClientsListFetchParams({
                            page: currentPage,
                            limit,
                            listingFilter,
                          }),
                        },
                      })
                    }
                    name="Add Client"
                    tooltip="Add Client"
                  />
                )}
            </div>
          </div>
        )}

      {/* Content */}
      {location.pathname === ROUTES.CLIENTS_ADD ? (
        <AddClient />
      ) : location.pathname.startsWith(`${ROUTES.CLIENTS_EDIT}/`) ? (
        <EditClient />
      ) : activeView === "dashboard" ? (
        <RequirePermission permission={PERMISSION_KEYS.CLIENTS_DASHBOARD_VIEW}>
          <ClientDashboard client={selectedClient} refreshToken={dashboardRefreshToken} />
        </RequirePermission>
      ) : !canUseClientsList ? (
        <PermissionDenied {...getPageDeniedContent(PERMISSION_KEYS.CLIENT_VIEW)} />
      ) : (
        <ClientList
          clientsData={{ list, meta }}
          projectsData={projectsData}
          loading={loading}
          searchTerm={searchTerm}
          currentPage={currentPage}
          setCurrentPage={handlePageChange}
          limit={limit}
          onPageSizeChange={handlePageSizeChange}
          listingFilter={listingFilter}
          clientsFetchParams={buildClientsListFetchParams({
            page: currentPage,
            limit,
            listingFilter,
          })}
          handleFilterChange={handleFilterChange}
          handleFilterReset={handleFilterReset}
          applyFilters={applyFilters}
          onRefetchClients={() => fetchClientsWithFilters(currentPage)}
          onClientClick={(client) => {
            if (!canViewClientDashboard) return;
            const params = new URLSearchParams();
            params.set("view", "dashboard");
            if (client?.id) {
              params.set("clientId", client.id);
            }
            navigate(`${ROUTES.CLIENTS}?${params.toString()}`);
          }}
        />
      )}
    </div>
  );
};

export default Clients;
